// Pure view helpers for the procurement detail page, kept out of the .svelte
// component so they can be unit-tested without rendering (components/views are
// otherwise only exercised by e2e — see vitest.config coverage note).
import type { TranslateParams } from '../../i18n'
import type { SourceErrorKind } from '../../api/procurements'

export type Translate = (key: string, params?: TranslateParams) => string

/**
 * Fixed, translated copy for a criteria-match failure classified as
 * 'rate_limited' (EOJN HTTP 429) — replaces the raw exception text, which
 * used to vary by internal retry-budget vs cooldown variant. Takes no code
 * parameter since there's only one value today; kept as a function (rather
 * than an inline `t()` call at the call site) so a second code slots in
 * later without touching the template.
 */
export function criteriaErrorMessage(t: Translate): string {
  return t('errors.eojnDownloadFailed')
}

/**
 * Human-readable "try again in ~X min" estimate counted down to an absolute
 * retry deadline, or null once that deadline has passed. `retryAfterUntil` is
 * a fixed point in time computed once at failure time — deliberately not
 * re-derived here from a relative seconds count plus "now", which would keep
 * claiming e.g. "~20 min" no matter how long after the failure the tender is
 * viewed. Callers must hide the estimate entirely when this returns null.
 */
export function retryEstimateMessage(
  retryAfterUntil: string,
  t: Translate,
  now: Date = new Date(),
): string | null {
  const remainingMs = new Date(retryAfterUntil).getTime() - now.getTime()
  if (remainingMs <= 0) return null
  const minutes = Math.max(1, Math.round(remainingMs / 60_000))
  return t('errors.eojnRetryEstimate', { minutes })
}

/**
 * Friendly copy for a document/source-retrieval failure the ingestion
 * pipeline classified by HTTP status, replacing the raw exception text shown
 * for an unclassified (generic) failure.
 */
export function sourceStatusMessage(kind: SourceErrorKind, t: Translate): string {
  return t(
    kind === 'access_denied'
      ? 'views.procurementDetail.aiScore.accessDenied'
      : 'views.procurementDetail.aiScore.sourceUnavailable',
  )
}

/** Badge label + tooltip for a source link whose page is known unreachable. */
export function sourceStatusBadge(
  kind: SourceErrorKind,
  t: Translate,
): { label: string; tooltip: string } {
  return kind === 'access_denied'
    ? {
        label: t('views.procurementDetail.sourceStatus.accessDeniedBadge'),
        tooltip: t('views.procurementDetail.sourceStatus.accessDeniedTooltip'),
      }
    : {
        label: t('views.procurementDetail.sourceStatus.unavailableBadge'),
        tooltip: t('views.procurementDetail.sourceStatus.unavailableTooltip'),
      }
}

/** The subset of a criterion verdict the breakdown row needs to render. */
export interface CriterionLike {
  criterion: string
  satisfied: boolean
  justification?: string
  // Absent on rows written before the evidence field existed (legacy).
  evidence?: string
}

/**
 * The reason shown under a criterion: the model's own justification when it
 * wrote one, otherwise a generic sentence keyed off the verdict. Trims so a
 * whitespace-only justification falls back rather than rendering blank.
 */
export function criterionReason(c: CriterionLike, t: Translate): string {
  if (c.justification && c.justification.trim()) return c.justification.trim()
  return t(
    c.satisfied
      ? 'views.procurementDetail.aiScore.metReason'
      : 'views.procurementDetail.aiScore.unmetReason',
    { criterion: c.criterion },
  )
}

/**
 * The verbatim company-profile snippet backing a satisfied verdict, or null
 * when the row carries none — an unmet verdict, or a legacy row written before
 * the field existed. Callers render the quote only when this is non-null.
 */
export function criterionEvidence(c: CriterionLike): string | null {
  const evidence = c.evidence?.trim()
  return evidence ? evidence : null
}

/**
 * The current score is highlighted by default. Selecting a historical snapshot
 * moves the standard score colour to that entry and mutes every other entry.
 */
export function historyScoreIsHighlighted(
  entryId: number,
  current: boolean,
  selectedHistoryId: number | null,
): boolean {
  return selectedHistoryId === null ? current : selectedHistoryId === entryId
}

export interface CriteriaSnapshotLike {
  requiredCriteria: unknown
  matchDetail: Array<{ criterion: string; category: string; satisfied: boolean }> | null
}

export interface ScoreSnapshotLike extends CriteriaSnapshotLike {
  matchPercentage: number
}

export interface CriterionChange {
  criterion: string
  category: string
}

export interface CriteriaDiff {
  added: CriterionChange[]
  removed: CriterionChange[]
  newlyMatched: CriterionChange[]
  noLongerMatched: CriterionChange[]
}

const REQUIRED_CRITERIA_CATEGORIES: Array<[field: string, category: string]> = [
  ['technologies', 'technology'],
  ['company_certificates', 'company_certificate'],
  ['expert_certificates', 'expert_certificate'],
  ['business_requirements', 'business_requirement'],
  ['experience_requirements', 'experience_requirement'],
  ['staffing_requirements', 'staffing_requirement'],
  ['service_requirements', 'service_requirement'],
  ['domain_requirements', 'domain_requirement'],
]

/** Compare every scored criterion and verdict in two adjacent analyses. */
export function criteriaDiff(
  current: CriteriaSnapshotLike,
  previous: CriteriaSnapshotLike | undefined,
): CriteriaDiff {
  const empty = { added: [], removed: [], newlyMatched: [], noLongerMatched: [] }
  if (!previous) return empty

  const currentCriteria = criteriaByKey(current)
  const previousCriteria = criteriaByKey(previous)
  const newlyMatched: CriterionChange[] = []
  const noLongerMatched: CriterionChange[] = []
  for (const [key, currentCriterion] of currentCriteria) {
    const previousCriterion = previousCriteria.get(key)
    if (!previousCriterion) continue
    if (previousCriterion.satisfied === false && currentCriterion.satisfied === true) {
      newlyMatched.push(toCriterionChange(currentCriterion))
    } else if (previousCriterion.satisfied === true && currentCriterion.satisfied === false) {
      noLongerMatched.push(toCriterionChange(currentCriterion))
    }
  }

  return {
    added: difference(currentCriteria, previousCriteria),
    removed: difference(previousCriteria, currentCriteria),
    newlyMatched: sortChanges(newlyMatched),
    noLongerMatched: sortChanges(noLongerMatched),
  }
}

/** Build the localized score explanation from the same two snapshots as the diff. */
export function scoreChangeExplanation(
  current: ScoreSnapshotLike,
  previous: ScoreSnapshotLike | undefined,
  t: Translate,
): string | null {
  if (!previous || current.matchPercentage === previous.matchPercentage) return null

  const currentCounts = criterionCounts(current)
  const previousCounts = criterionCounts(previous)
  const key =
    current.matchPercentage > previous.matchPercentage
      ? 'views.procurementDetail.aiScore.historyScoreIncreased'
      : 'views.procurementDetail.aiScore.historyScoreDecreased'
  return t(key, {
    previousScore: previous.matchPercentage,
    currentScore: current.matchPercentage,
    currentMatched: currentCounts.matched,
    currentTotal: currentCounts.total,
    previousMatched: previousCounts.matched,
    previousTotal: previousCounts.total,
  })
}

interface CriterionState extends CriterionChange {
  satisfied: boolean | null
}

function criteriaByKey(snapshot: CriteriaSnapshotLike): Map<string, CriterionState> {
  const criteria = new Map<string, CriterionState>()
  // matchDetail is the exact scored set and therefore must be authoritative so
  // the diff reconciles with the numerator/denominator shown in the explanation.
  if (snapshot.matchDetail !== null) {
    for (const match of snapshot.matchDetail) {
      addCriterion(criteria, match.criterion, match.category, match.satisfied)
    }
    return criteria
  }

  // Legacy snapshots can lack matchDetail; reconstruct their criteria from the
  // extracted category arrays as a best-effort fallback.
  const requiredCriteria =
    snapshot.requiredCriteria &&
    typeof snapshot.requiredCriteria === 'object' &&
    !Array.isArray(snapshot.requiredCriteria)
      ? (snapshot.requiredCriteria as Record<string, unknown>)
      : null

  for (const [field, category] of REQUIRED_CRITERIA_CATEGORIES) {
    const values = requiredCriteria?.[field]
    if (!Array.isArray(values)) continue
    for (const criterion of values) addCriterion(criteria, criterion, category, null)
  }
  return criteria
}

function criterionCounts(snapshot: CriteriaSnapshotLike): { matched: number; total: number } {
  if (snapshot.matchDetail !== null) {
    return {
      matched: snapshot.matchDetail.filter((criterion) => criterion.satisfied).length,
      total: snapshot.matchDetail.length,
    }
  }
  const criteria = [...criteriaByKey(snapshot).values()]
  return {
    matched: criteria.filter((criterion) => criterion.satisfied === true).length,
    total: criteria.length,
  }
}

function addCriterion(
  criteria: Map<string, CriterionState>,
  value: unknown,
  category: string,
  satisfied: boolean | null,
) {
  if (typeof value !== 'string') return
  const criterion = value.trim().replace(/\s+/g, ' ')
  if (!criterion) return
  const key = `${category.toLowerCase()}\u0000${criterion.toLowerCase()}`
  criteria.set(key, { criterion, category, satisfied })
}

function difference(
  left: Map<string, CriterionState>,
  right: Map<string, CriterionState>,
): CriterionChange[] {
  return sortChanges(
    [...left]
      .filter(([key]) => !right.has(key))
      .map(([, criterion]) => toCriterionChange(criterion)),
  )
}

function toCriterionChange(state: CriterionState): CriterionChange {
  return { criterion: state.criterion, category: state.category }
}

function sortChanges(changes: CriterionChange[]): CriterionChange[] {
  return changes.sort(
    (a, b) => a.criterion.localeCompare(b.criterion) || a.category.localeCompare(b.category),
  )
}
